import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { hasAdminSession } from '@/lib/adminAuth';

const Navbar = ({ onContactClick, alwaysSolid = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/' && !location.hash;
    if (href.includes('#')) return (location.pathname + location.hash) === href;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Blog', href: '/blog' },
    { name: 'Case Studies', href: '/case-studies' },
    { name: 'Newsletter', href: '/newsletter' },
    { name: 'Services', href: '/#services' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Results', href: '/#proven-results' },
  ];
  const showAdminLink = hasAdminSession();
  const visibleNavLinks = showAdminLink
    ? [...navLinks, { name: 'Admin', href: '/admin/newsletter' }]
    : navLinks;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || alwaysSolid
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5' 
          : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            data-testid="nav-logo"
          >
            <Link to="/">
              <img
                src="/assets/logos/logo-wordmark.png"
                alt="Grawish Logo"
                className="h-12 w-auto"
              />
            </Link>
          </motion.div>

          <div className="hidden md:flex items-center gap-10">
            {visibleNavLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <motion.a
                  key={link.name}
                  href={link.href}
                  className={`text-sm font-semibold relative group transition-colors ${active ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'}`}
                  whileHover={{ y: -2 }}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                  
                  {/* Bottom underline */}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 ${active ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                </motion.a>
              );
            })}
          </div>

          <motion.button
            onClick={() => (onContactClick ? onContactClick() : window.location.assign('/'))}
            className="hidden md:flex primary-btn px-7 py-3 rounded-full text-white font-semibold text-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="nav-cta-btn"
          >
            Book Consultation
          </motion.button>

          <button
            className="md:hidden p-2 rounded-lg text-gray-800"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100 shadow-xl"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {visibleNavLinks.map((link) => {
                const active = isActive(link.href);
                return (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`block text-base font-medium transition-colors border-l-4 pl-3 ${active ? 'text-purple-600 border-pink-500 bg-purple-50 rounded-r-lg py-1' : 'text-gray-700 hover:text-purple-600 border-transparent py-1'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                );
              })}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  if (onContactClick) {
                    onContactClick();
                    return;
                  }
                  window.location.assign('/');
                }}
                className="block w-full primary-btn px-6 py-3 rounded-full text-white font-semibold text-center mt-4"
                data-testid="mobile-cta-btn"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
